import { useEffect, useMemo, useState } from "react";
import ReactDOM from "react-dom";
import { Button } from "../../components/Button/Button";
import { PageLayout } from "../../components/layout/PageLayout";
import { showErrorToast, showSuccessToast } from "../../lib/ToastUtility";
import { useKioskTransactions } from "../../hooks/useKioskTransactions";
import { fetchAllCampaigns } from "../../services/CampaignService";
import {
  getKioskPayoutLedger,
  redeemKioskPayout,
} from "../../services/KioskTransactionsService";
import {
  KioskPayoutLedgerResponse,
} from "../../types/kioskTransactions.type";

const payoutProgramOptions = [
  {
    key: "humanitarian-food-programmes",
    label: "Humanitarian food programmes",
  },
  {
    key: "child-nutrition-programme",
    label: "Child Nutrition Programme",
  },
  {
    key: "emergency-relief-programme",
    label: "Emergency Relief Programme",
  },
] as const;

const emptyLedger: KioskPayoutLedgerResponse = {
  ownerEmail: "",
  kioskId: null,
  entryCount: 0,
  summary: {
    totalEarnedCents: 0,
    totalRedeemedCents: 0,
    availableBalanceCents: 0,
  },
  entries: [],
};

const formatCentsUsd = (value: number) => `$${(Math.max(0, value) / 100).toFixed(2)}`;
const formatLedgerDetails = (details: unknown) => {
  if (!details) return "-";

  if (typeof details === "string") return details;

  if (Array.isArray(details)) {
    return details
      .map((item) => (typeof item === "string" ? item : JSON.stringify(item)))
      .join(" • ");
  }

  if (typeof details === "object") {
    return Object.entries(details as Record<string, unknown>)
      .map(([key, value]) => `${key}: ${String(value)}`)
      .join(" • ");
  }

  return String(details);
};
const programRedeemUnitCents = 200;

const OwnerRewardsPage = () => {
  const { summary } = useKioskTransactions();
  const [loadingPayoutLedger, setLoadingPayoutLedger] = useState(false);
  const [loadingCampaigns, setLoadingCampaigns] = useState(false);
  const [redeemingPayout, setRedeemingPayout] = useState(false);
  const [payoutLedgerError, setPayoutLedgerError] = useState("");
  const [redeemAmountUsd, setRedeemAmountUsd] = useState("");
  const [selectedPayoutMode, setSelectedPayoutMode] = useState<"CASH" | "PROGRAM">("PROGRAM");
  const [selectedCampaignId, setSelectedCampaignId] = useState("");
  const [campaigns, setCampaigns] = useState<Array<{ id: number; name: string }>>([]);
  const [payoutLedger, setPayoutLedger] = useState<KioskPayoutLedgerResponse>(emptyLedger);
  const [ledgerModalOpen, setLedgerModalOpen] = useState(false);
  const ownerPreferences = summary.ownerPreferences;
  const redeemAmountCents = Math.round((Number(redeemAmountUsd) || 0) * 100);
  const isProgramAmountValid =
    selectedPayoutMode !== "PROGRAM" ||
    (redeemAmountCents >= programRedeemUnitCents && redeemAmountCents % programRedeemUnitCents === 0);

  const selectedProgram = useMemo(
    () =>
      payoutProgramOptions.find(
        (option) => option.key === ownerPreferences.payoutProgramKey,
      ),
    [ownerPreferences.payoutProgramKey],
  );

  const loadPayoutLedger = async () => {
    setLoadingPayoutLedger(true);
    setPayoutLedgerError("");

    try {
      const data = await getKioskPayoutLedger();
      setPayoutLedger(data);
    } catch (error) {
      setPayoutLedgerError((error as Error).message || "Failed to load payout ledger");
    } finally {
      setLoadingPayoutLedger(false);
    }
  };

  const loadCampaigns = async () => {
    setLoadingCampaigns(true);

    try {
      const response = await fetchAllCampaigns("");
      const items = Array.isArray(response?.data) ? response.data : [];
      const nextCampaigns = items
        .filter((campaign: any) => campaign?.id && campaign?.name)
        .map((campaign: any) => ({ id: Number(campaign.id), name: String(campaign.name) }));

      setCampaigns(nextCampaigns);
      setSelectedCampaignId((current) => current || (nextCampaigns[0] ? String(nextCampaigns[0].id) : ""));
    } catch {
      setCampaigns([]);
    } finally {
      setLoadingCampaigns(false);
    }
  };

  useEffect(() => {
    void loadPayoutLedger();
  }, []);

  useEffect(() => {
    setSelectedPayoutMode(ownerPreferences.payoutMode);
  }, [ownerPreferences.payoutMode]);

  useEffect(() => {
    if (selectedPayoutMode === "PROGRAM") {
      void loadCampaigns();
    }
  }, [selectedPayoutMode]);

  const redeemPayout = async () => {
    const amount = Number(redeemAmountUsd);

    if (!Number.isFinite(amount) || amount <= 0) {
      showErrorToast("Enter a valid redeem amount in USD.");
      return;
    }

    const amountCents = Math.round(amount * 100);
    if (amountCents > payoutLedger.summary.availableBalanceCents) {
      showErrorToast("Redeem amount exceeds available payout balance.");
      return;
    }

    if (selectedPayoutMode === "PROGRAM" && !selectedCampaignId) {
      showErrorToast("Select a campaign before redeeming into a programme.");
      return;
    }

    if (
      selectedPayoutMode === "PROGRAM" &&
      (amountCents < programRedeemUnitCents || amountCents % programRedeemUnitCents !== 0)
    ) {
      showErrorToast("Programme redemption amounts must be at least $2.00 and use $2.00 increments.");
      return;
    }

    setRedeemingPayout(true);
    try {
      await redeemKioskPayout({
        amountCents,
        mode: selectedPayoutMode,
        campaignId: selectedPayoutMode === "PROGRAM" ? Number(selectedCampaignId) : undefined,
        payoutProgramKey:
          selectedPayoutMode === "PROGRAM"
            ? ownerPreferences.payoutProgramKey
            : undefined,
        payoutProgramName:
          selectedPayoutMode === "PROGRAM"
            ? ownerPreferences.payoutProgramName
            : undefined,
      });

      setRedeemAmountUsd("");
      showSuccessToast("Reward redeemed successfully.");
      await loadPayoutLedger();
    } catch (error) {
      showErrorToast((error as Error).message || "Failed to redeem reward");
    } finally {
      setRedeemingPayout(false);
    }
  };

  return (
    <PageLayout title="Rewards" subtitle="Track earned rewards and redeem balances">
      <div className="p-6 space-y-6">
        <div className="rounded-[28px] border border-emerald-100 bg-gradient-to-br from-white via-emerald-50/70 to-sky-50/80 p-6 shadow-[0_24px_60px_rgba(16,63,36,0.08)] space-y-5 dark:border-white/10 dark:bg-slate-900/70">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-xs">
            <div className="overflow-hidden rounded-2xl border border-emerald-100 bg-white px-5 py-5 shadow-sm dark:border-white/10 dark:bg-slate-800/80">
              <p className="text-[11px] font-semibold uppercase tracking-[0.22em] text-emerald-600 dark:text-emerald-300">Earned</p>
              <p className="mt-3 text-3xl font-semibold text-slate-900 dark:text-gray-100">
                {formatCentsUsd(payoutLedger.summary.totalEarnedCents)}
              </p>
              <p className="mt-2 text-sm leading-6 text-slate-500 dark:text-slate-300">
                Commission generated from monthly kiosk billing.
              </p>
            </div>
            <div className="overflow-hidden rounded-2xl border border-amber-100 bg-white px-5 py-5 shadow-sm dark:border-white/10 dark:bg-slate-800/80">
              <p className="text-[11px] font-semibold uppercase tracking-[0.22em] text-amber-600 dark:text-amber-300">Redeemed</p>
              <p className="mt-3 text-3xl font-semibold text-slate-900 dark:text-gray-100">
                {formatCentsUsd(payoutLedger.summary.totalRedeemedCents)}
              </p>
              <p className="mt-2 text-sm leading-6 text-slate-500 dark:text-slate-300">
                Already allocated to cash or humanitarian programme redemptions.
              </p>
            </div>
            <div className="overflow-hidden rounded-2xl border border-emerald-200 bg-gradient-to-br from-emerald-600 to-teal-600 px-5 py-5 shadow-lg shadow-emerald-900/10 dark:border-emerald-500/30">
              <p className="text-[11px] font-semibold uppercase tracking-[0.22em] text-emerald-100">Available</p>
              <p className="mt-3 text-3xl font-semibold text-white">
                {formatCentsUsd(payoutLedger.summary.availableBalanceCents)}
              </p>
              <p className="mt-2 text-sm leading-6 text-emerald-50/90">
                Available balance = earned above licence fees minus redeemed rewards.
              </p>
            </div>
          </div>

          <div className="flex justify-end">
            <Button
              value="View Full Redeem Ledger"
              variant="secondary"
              onClick={() => setLedgerModalOpen(true)}
              disabled={loadingPayoutLedger}
            />
          </div>

          {payoutLedgerError && (
            <div className="rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-700">
              {payoutLedgerError}
            </div>
          )}

          <div>
            <label className="block text-xs font-medium mb-1 text-gray-700 dark:text-gray-300">
              Payout Mode
            </label>
            <select
              value={selectedPayoutMode}
              onChange={(event) =>
                setSelectedPayoutMode(event.target.value === "PROGRAM" ? "PROGRAM" : "CASH")
              }
              className="w-full px-3 py-2 rounded-lg border border-gray-300 dark:border-white/15 bg-white dark:bg-slate-800 text-sm"
            >
              <option value="PROGRAM">Allocated to humanitarian food programmes</option>
              <option value="CASH">Cash payout</option>
            </select>
            {selectedPayoutMode === "PROGRAM" && (
              <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                {selectedProgram?.label || ownerPreferences.payoutProgramName}
              </p>
            )}
          </div>

          {selectedPayoutMode === "PROGRAM" && (
            <div>
              <label className="block text-xs font-medium mb-1 text-gray-700 dark:text-gray-300">
                Campaign
              </label>
              <select
                value={selectedCampaignId}
                onChange={(event) => setSelectedCampaignId(event.target.value)}
                disabled={loadingCampaigns}
                className="w-full px-3 py-2 rounded-lg border border-gray-300 dark:border-white/15 bg-white dark:bg-slate-800 text-sm"
              >
                <option value="">{loadingCampaigns ? "Loading campaigns..." : "Select a campaign"}</option>
                {campaigns.map((campaign) => (
                  <option key={campaign.id} value={String(campaign.id)}>
                    {campaign.name}
                  </option>
                ))}
              </select>
            </div>
          )}

          <div>
            <label className="block text-xs font-medium mb-1 text-gray-700 dark:text-gray-300">
              Redeem Amount (USD)
            </label>
            <input
              type="number"
              min="0"
              step="0.01"
              value={redeemAmountUsd}
              onChange={(event) => setRedeemAmountUsd(event.target.value)}
              className="w-full px-3 py-2 rounded-lg border border-gray-300 dark:border-white/15 bg-white dark:bg-slate-800 text-sm"
              placeholder="0.00"
            />
            {selectedPayoutMode === "PROGRAM" && (
              <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                Programme redemptions are allocated in $2.00 food-unit increments.
              </p>
            )}
            {selectedPayoutMode === "PROGRAM" && redeemAmountUsd && !isProgramAmountValid && (
              <p className="mt-1 text-xs text-red-600 dark:text-red-300">
                Enter at least $2.00 and use $2.00 increments for programme redemptions.
              </p>
            )}
          </div>

          <div className="flex justify-end pt-1">
            <Button
              value="Redeem"
              onClick={() => {
                void redeemPayout();
              }}
              disabled={loadingPayoutLedger || redeemingPayout || payoutLedger.summary.availableBalanceCents <= 0 || (selectedPayoutMode === "PROGRAM" && (!selectedCampaignId || !isProgramAmountValid))}
              loading={redeemingPayout}
            />
          </div>

        </div>
      </div>

      {ledgerModalOpen && ReactDOM.createPortal(
        <div
          className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/55 p-4"
          onClick={() => setLedgerModalOpen(false)}
        >
          <div
            className="w-full max-w-6xl max-h-[88vh] overflow-hidden rounded-2xl border border-white/70 bg-white shadow-2xl dark:border-white/10 dark:bg-slate-900"
            onClick={(event) => event.stopPropagation()}
          >
            <div className="sticky top-0 z-10 flex items-center justify-between border-b border-slate-200 bg-white/95 px-5 py-4 backdrop-blur dark:border-white/10 dark:bg-slate-900/95">
              <div>
                <p className="text-lg font-semibold text-slate-900 dark:text-white">Payout Ledger</p>
                <p className="text-xs text-slate-500 dark:text-slate-400">Most recent transactions first</p>
              </div>
              <Button value="Close" variant="tertiary" onClick={() => setLedgerModalOpen(false)} />
            </div>

            <div className="overflow-auto p-5">
              <table className="min-w-full text-sm">
                <thead>
                  <tr className="border-b border-slate-200 text-left text-xs uppercase tracking-wide text-slate-500 dark:border-white/10 dark:text-slate-400">
                    <th className="px-2 py-2">Date</th>
                    <th className="px-2 py-2">Type</th>
                    <th className="px-2 py-2">Owner</th>
                    <th className="px-2 py-2">Kiosk</th>
                    <th className="px-2 py-2 text-right">Amount</th>
                    <th className="px-2 py-2 text-right">Running Balance</th>
                  </tr>
                </thead>
                <tbody>
                  {payoutLedger.entries.length === 0 && (
                    <tr>
                      <td colSpan={6} className="px-2 py-6 text-center text-slate-500 dark:text-slate-400">
                        No ledger entries found.
                      </td>
                    </tr>
                  )}

                  {[...payoutLedger.entries]
                    .sort((a, b) => new Date(b.occurredAt).getTime() - new Date(a.occurredAt).getTime())
                    .map((entry) => (
                      <tr key={entry.key} className="border-b border-slate-100 align-top dark:border-white/5">
                        <td className="px-2 py-3 whitespace-nowrap text-slate-700 dark:text-slate-300">
                          {new Date(entry.occurredAt).toLocaleString()}
                        </td>
                        <td className="px-2 py-3">
                          <div className="flex flex-col gap-1">
                            <span className="w-fit rounded-full border border-slate-300 bg-slate-50 px-2 py-1 text-xs font-medium text-slate-700 dark:border-white/20 dark:bg-slate-800 dark:text-slate-300">
                              {entry.entryType}
                            </span>
                            {entry.details && (
                              <span className="text-xs leading-5 text-slate-500 dark:text-slate-400">
                                {formatLedgerDetails(entry.details)}
                              </span>
                            )}
                          </div>
                        </td>
                        <td className="px-2 py-3 text-slate-700 dark:text-slate-300">{entry.ownerEmail || "-"}</td>
                        <td className="px-2 py-3 text-slate-700 dark:text-slate-300">{entry.kioskName || entry.kioskId || "-"}</td>
                        <td className="px-2 py-3 text-right font-semibold text-slate-900 dark:text-white">
                          {formatCentsUsd(entry.amountCents)}
                        </td>
                        <td className="px-2 py-3 text-right font-semibold text-emerald-700 dark:text-emerald-300">
                          {formatCentsUsd(entry.runningAvailableCents)}
                        </td>
                      </tr>
                    ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>,
        document.body,
      )}
    </PageLayout>
  );
};

export default OwnerRewardsPage;